import type { SettingTerm } from '@/lib/settings-search/types'
import type { ComponentType } from 'react'
import { useMemo } from 'react'
import { MODULE_SETTINGS_PATHS } from '@/config/settings-directory'
import { SETTINGS_SEARCH_INDEX } from '@/config/settings-search-index.generated'
import {
  ADMIN_SETTINGS_SECTIONS,
  MODULE_SETTINGS_SECTIONS,
  USER_SETTINGS_TABS,
} from '@/config/settings-sections'
import { matchSettingsItem } from '@/lib/settings-search/match'
import { WHOLE_PAGE_SECTION } from '@/lib/settings-search/types'
import { GearSixIcon } from '@phosphor-icons/react'

import { useSettingsDirectory } from './useSettingsDirectory'
import { useVisibleNavItems } from './useVisibleNavItems'

export interface OmniNavTarget {
  id: string
  label: string
  /** Settings rendered on this page or section, from the generated search index. */
  settings?: readonly SettingTerm[]
  /** On matched results: the setting the query reached, if not the label itself. */
  matchedSetting?: SettingTerm
  /** Org-relative path — pass through getOrgPath() before navigating */
  path: string
  icon: ComponentType<{ className?: string }>
  group: 'module' | 'settings'
  keywords: readonly string[]
}

// Extra search aliases per module href so "vendors" finds Suppliers, etc.
const MODULE_KEYWORDS: Record<string, string[]> = {
  '/today': ['overview', 'home', 'dashboard'],
  '/tasks/mine': ['my work', 'my day', 'tasks'],
  '/notes/mine': ['notes'],
  '/my-time': ['time tracking', 'timer', 'hours'],
  '/calendar': ['schedule', 'planner'],
  '/my-organization': ['home base', 'organization', 'org'],
  '/projects': ['folders'],
  '/tasks': ['todo', 'work'],
  '/tickets': ['helpdesk', 'support'],
  '/knowledge-base': ['kb', 'articles', 'wiki', 'help'],
  '/collections': ['database', 'lists'],
  '/forms': ['surveys'],
  '/threads': ['discussions', 'chat'],
  '/documents': ['files', 'docs'],
  '/events': ['calendar'],
  '/contacts': ['crm', 'people', 'address book', 'leads'],
  '/deals': ['pipeline', 'sales', 'crm'],
  '/marketing': ['campaigns', 'email', 'journeys'],
  '/scheduling': ['booking', 'appointments'],
  '/live-chat': ['chat', 'widget'],
  '/quotes': ['proposals', 'estimates'],
  '/products': ['price book', 'catalog'],
  '/communications': ['calls', 'calling', 'phone', 'sequences'],
  '/entities': ['clients', 'companies', 'accounts'],
  '/suppliers': ['vendors'],
  '/team': ['members', 'staff', 'people'],
  '/timesheets': ['time approval', 'hours'],
  '/workflows': ['automation'],
  '/contracts': ['agreements'],
  '/invoices': ['billing'],
  '/expenses': ['receipts', 'spend'],
}

const ADMIN_LABELS: Record<string, string> = {
  '/admin/members': 'Organization Members',
  '/admin/integrations': 'Integrations',
  '/admin/settings': 'Organization Settings',
  '/admin/logs': 'Audit Trail',
  '/admin/archive': 'Archive',
  '/admin/data': 'Bring your data',
  '/admin/subscription': 'Subscription & Billing',
}

const ADMIN_KEYWORDS: Record<string, string[]> = {
  '/admin/members': ['admin', 'users', 'invite', 'roles', 'permissions'],
  '/admin/integrations': [
    'admin',
    'connect',
    'slack',
    'github',
    'quickbooks',
    'outlook',
  ],
  '/admin/settings': [
    'admin',
    'general',
    'workspace',
    'organization',
    'finance',
  ],
  '/admin/logs': [
    'admin',
    'audit',
    'log',
    'history',
    'compliance',
    'who changed',
    'access',
  ],
  '/admin/archive': [
    'admin',
    'archive',
    'archived',
    'deleted',
    'restore',
    'recover',
    'undelete',
    'recycle bin',
    'trash',
  ],
  '/admin/data': [
    'admin',
    'data',
    'import',
    'migrate',
    'migration',
    'move data',
    'harvest',
    'toggl',
    'clockify',
    'monday',
    'accelo',
    'scoro',
  ],
  '/admin/subscription': [
    'admin',
    'billing',
    'plan',
    'payment',
    'upgrade',
    'seats',
  ],
}

/**
 * Navigation destinations (modules + settings pages) searchable from
 * OmniSearch. Applies the same visibility gating as AppSidebar
 * (accountTypes → featureFlag, else subscriptionFeature → permission), but
 * includes `hidden` nav items — search is exactly where non-sidebar
 * destinations should stay discoverable.
 */
export function useOmniNavTargets(): OmniNavTarget[] {
  const {
    visibleMainNavItems,
    adminNavItems,
    userMenuItems,
    isAdminOrOwner,
    isPersonal,
    checkFeatureFlag,
    accountType,
  } = useVisibleNavItems()
  const settingsDirectory = useSettingsDirectory()

  return useMemo(() => {
    const visibleModules = visibleMainNavItems

    const moduleTargets: OmniNavTarget[] = visibleModules.map(item => ({
      id: `module:${item.href}`,
      label: item.label,
      path: item.href,
      icon: item.icon,
      group: 'module',
      keywords: MODULE_KEYWORDS[item.href] ?? [],
    }))

    const settingsTargets: OmniNavTarget[] = []

    // The unified settings hub — the directory of everything below.
    settingsTargets.push({
      id: 'settings:/settings',
      label: 'Settings',
      path: '/settings',
      icon: GearSixIcon,
      group: 'settings',
      keywords: [
        'settings',
        'preferences',
        'hub',
        'all settings',
        'workspace',
        'organization',
      ],
    })

    const moduleSettingsByHref = new Map(
      [...settingsDirectory.personal, ...settingsDirectory.org].map(group => [
        group.entry.navHref,
        group,
      ]),
    )
    // Use the same permission-filtered directory as the settings shell.
    // Section entries deep-link via ?section= (pages resolve it through
    // useSettingsSection or their own param handling).
    for (const item of visibleModules) {
      const settingsPath = MODULE_SETTINGS_PATHS[item.href]
      const settingsGroup = moduleSettingsByHref.get(item.href)
      if (!settingsPath || !settingsGroup) continue
      settingsTargets.push({
        id: `settings:${settingsPath}`,
        label: `${item.label} Settings`,
        path: settingsPath,
        icon: item.icon,
        group: 'settings',
        keywords: [
          'settings',
          'preferences',
          'configure',
          ...(MODULE_KEYWORDS[item.href] ?? []),
        ],
        settings:
          SETTINGS_SEARCH_INDEX.modules[item.href]?.[WHOLE_PAGE_SECTION],
      })

      const sectionConfig = MODULE_SETTINGS_SECTIONS[item.href]
      if (!sectionConfig) continue
      const param = sectionConfig.param ?? 'section'
      for (const section of sectionConfig.sections) {
        if (
          !settingsGroup.sections.some(visible => visible.id === section.id)
        ) {
          continue
        }
        settingsTargets.push({
          id: `settings:${settingsPath}:${section.id}`,
          label: `${item.label} · ${section.label}`,
          path: `${settingsPath}?${param}=${section.id}`,
          icon: item.icon,
          group: 'settings',
          // No generic 'settings' keyword here — a bare "settings" query
          // should surface pages, not flood with every section.
          keywords: [
            ...(section.keywords ?? []),
            ...(MODULE_KEYWORDS[item.href] ?? []),
          ],
          settings: SETTINGS_SEARCH_INDEX.modules[item.href]?.[section.id],
        })
      }
    }

    for (const item of userMenuItems) {
      settingsTargets.push({
        id: `settings:${item.href}`,
        label: item.label,
        path: item.href,
        icon: item.icon,
        group: 'settings',
        keywords: [
          'settings',
          'account',
          'profile',
          'preferences',
          'integrations',
          'skills',
          'email',
          'timezone',
        ],
      })
      if (item.href === '/user-settings') {
        for (const tab of USER_SETTINGS_TABS) {
          settingsTargets.push({
            id: `settings:/user-settings:${tab.id}`,
            label: `${item.label} · ${tab.label}`,
            path: `/settings/personal#${tab.id}`,
            icon: item.icon,
            group: 'settings',
            keywords: tab.keywords ?? [],
            settings: SETTINGS_SEARCH_INDEX.pages['personal:profile']?.[tab.id],
          })
        }
      }
    }

    if (isAdminOrOwner) {
      for (const item of adminNavItems) {
        if (item.accountTypes && !item.accountTypes.includes(accountType)) {
          continue
        }
        settingsTargets.push({
          id: `settings:${item.href}`,
          label: ADMIN_LABELS[item.href] ?? item.label,
          // Administration pages open inside the Settings shell.
          path:
            item.href === '/admin/settings'
              ? '/settings'
              : `/settings/administration/${item.href.split('/').pop()}`,
          icon: item.icon,
          group: 'settings',
          keywords: ['settings', ...(ADMIN_KEYWORDS[item.href] ?? [])],
          settings:
            SETTINGS_SEARCH_INDEX.pages[`admin:${item.href}`]?.[
              WHOLE_PAGE_SECTION
            ],
        })
        if (item.href === '/admin/settings') {
          // Sections open on the Settings tab of the Settings shell, which
          // hosts the same page without entering admin mode.
          for (const section of ADMIN_SETTINGS_SECTIONS) {
            if (section.businessOnly && isPersonal) continue
            if (section.personalOnly && !isPersonal) continue
            if (section.featureFlag && !checkFeatureFlag?.(section.featureFlag))
              continue
            if (section.orgAdmin && !isAdminOrOwner) continue
            settingsTargets.push({
              id: `settings:/admin/settings:${section.id}`,
              label: `Organization Settings · ${section.label}`,
              path: `/settings?section=${section.id}`,
              icon: item.icon,
              group: 'settings',
              keywords: section.keywords ?? [],
              settings:
                SETTINGS_SEARCH_INDEX.pages['admin:/admin/settings']?.[
                  section.id
                ],
            })
          }
        }
      }
    }

    return [...moduleTargets, ...settingsTargets]
  }, [
    accountType,
    adminNavItems,
    isAdminOrOwner,
    isPersonal,
    checkFeatureFlag,
    userMenuItems,
    visibleMainNavItems,
    settingsDirectory,
  ])
}

/**
 * Match nav targets against a search query. Ranked: label prefix, word
 * prefix, label substring, keyword prefix, keyword substring. Single-char
 * queries only match label prefixes to keep noise down.
 */
export function matchNavTargets(
  targets: OmniNavTarget[],
  query: string,
): OmniNavTarget[] {
  if (!query.trim()) return []

  // Same ranking as the Settings hub search (lib/settings-search/match):
  // label, then keywords, then the settings inside a page or section.
  const scored: { target: OmniNavTarget; score: number }[] = []
  for (const target of targets) {
    const match = matchSettingsItem(target, query)
    if (!match) continue
    scored.push({
      target: match.matchedSetting
        ? { ...target, matchedSetting: match.matchedSetting }
        : target,
      score: match.score,
    })
  }

  return scored
    .sort(
      (a, b) =>
        a.score - b.score || a.target.label.length - b.target.label.length,
    )
    .map(entry => entry.target)
}
